import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ProductCard from "@/components/ProductCard";
import API, { getImageUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  ArrowRight,
  Sparkles,
  Feather,
  PackageCheck,
  Users,
  Star,
  CheckCircle2,
  MapPin,
  ChevronDown,
  ShieldCheck,
} from "lucide-react";

/* ─── Scroll-reveal hook using Intersection Observer ─── */
function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("revealed");
          observer.unobserve(el);
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return ref;
}

function RevealSection({ children, className = "", delay = "", id }) {
  const ref = useReveal();
  return (
    <div id={id} ref={ref} className={`landing-reveal ${delay} ${className}`}>
      {children}
    </div>
  );
}

/* ─── Fallback product data ─── */
const FALLBACK_PRODUCTS = [
  { id: "f1", name: "Gamis Sage Premium", price: 389000, category: "Gamis" },
  { id: "f2", name: "Set Couple Elegan", price: 599000, category: "Couple" },
  { id: "f3", name: "Hijab Voal Exclusive", price: 129000, category: "Hijab" },
  { id: "f4", name: "Family Set Harmoni", price: 899000, category: "Family Set" },
];

/* ─── Hero image ─── */
const DEFAULT_HERO_IMG = "/hero-preview-20260512.jpg";
const DEFAULT_HERO_MOBILE_IMG = "/hero-mobile-preview-20260512.jpg";
const PREORDER_IMG = getImageUrl("naura-numa/assets/frontend/preorder_highlight.png");
const PACKAGING_MAIN_IMG = getImageUrl("naura-numa/assets/frontend/landing/packaging-main.jpg");
const PACKAGING_DETAIL_IMG = getImageUrl("naura-numa/assets/frontend/landing/packaging-detail.jpg");
const PACKAGING_STACK_IMG = getImageUrl("naura-numa/assets/frontend/landing/packaging-stack.jpg");
const PACKAGING_SHIPMENT_IMG = getImageUrl("naura-numa/assets/frontend/landing/packaging-shipment.jpg");
const resolveHeroImageUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("/") || path.startsWith("http") || path.startsWith("data:")) return path;
  return getImageUrl(path);
};

/* ═══════════════════════════════════════════════════════════════
   Landing Page
   ═══════════════════════════════════════════════════════════════ */
export default function LandingPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [preorderProducts, setPreorderProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [heroSettings, setHeroSettings] = useState({
    hero_image_url: DEFAULT_HERO_IMG,
    hero_image_mobile_url: DEFAULT_HERO_MOBILE_IMG,
    hero_overlay_opacity: 35,
  });
  const [loaded, setLoaded] = useState(false);

  const isLoggedIn = !!user;
  const isReseller = ["reseller", "agen", "mitra", "produsen", "distributor", "admin"].includes(user?.role);

  const scrollToPartnerBrandCta = (event) => {
    event.preventDefault();
    const target = document.getElementById("gabung-mitra-brand");
    if (!target) return;
    const rect = target.getBoundingClientRect();
    const offset = window.innerHeight * 0.58;
    const top = window.scrollY + rect.top - offset;
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  };

  useEffect(() => {
    let mounted = true;
    Promise.allSettled([
      API.get("/products?limit=4"),
      API.get("/products?stock_type=preorder&limit=3"),
      API.get("/brands"),
      API.get("/tenant"),
    ]).then(([prodRes, poRes, brandRes, tenantRes]) => {
      if (!mounted) return;
      if (prodRes.status === "fulfilled" && prodRes.value?.data?.products?.length) {
        setProducts(prodRes.value.data.products);
      }
      if (poRes.status === "fulfilled" && poRes.value?.data?.products?.length) {
        setPreorderProducts(poRes.value.data.products);
      }
      if (brandRes.status === "fulfilled" && brandRes.value?.data?.brands?.length) {
        setBrands(brandRes.value.data.brands);
      }
      if (tenantRes.status === "fulfilled" && tenantRes.value?.data) {
        const tenant = tenantRes.value.data;
        setHeroSettings({
          hero_image_url: tenant.hero_image_url || DEFAULT_HERO_IMG,
          hero_image_mobile_url: tenant.hero_image_mobile_url || tenant.hero_image_url || DEFAULT_HERO_MOBILE_IMG,
          hero_overlay_opacity: Math.max(0, Math.min(100, Number(tenant.hero_overlay_opacity ?? 35))),
        });
      }
      setLoaded(true);
    });
    return () => { mounted = false; };
  }, []);


  const heroImg = resolveHeroImageUrl(heroSettings.hero_image_url || DEFAULT_HERO_IMG);
  const heroMobileImg = resolveHeroImageUrl(heroSettings.hero_image_mobile_url || heroSettings.hero_image_url || DEFAULT_HERO_MOBILE_IMG);
  const heroOverlayOpacity = Math.max(0, Math.min(100, Number(heroSettings.hero_overlay_opacity ?? 35))) / 100;

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="landing-page">
      <Header />

      {/* ───────────────── 1. HERO ───────────────── */}
      <section
        className="relative min-h-[calc(100svh-4rem)] md:min-h-[90vh] flex items-center overflow-hidden"
        data-testid="landing-hero"
      >
        {/* background image */}
        <div className="absolute inset-0">
          <picture>
            <source media="(max-width: 767px)" srcSet={heroMobileImg} />
            <img
              src={heroImg}
              alt="Haniyah Gallery Collection"
              className="w-full h-full object-cover object-center md:object-top"
            />
          </picture>
          {heroOverlayOpacity > 0 && (
            <div className="absolute inset-0 bg-stone-950" style={{ opacity: heroOverlayOpacity }} />
          )}
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24 w-full">
          <div className="max-w-2xl">
            {/* CTAs */}
            <div className="landing-hero-anim landing-hero-anim-d4 mt-14 sm:mt-16 flex flex-col sm:flex-row gap-4">
              <Link
                to="/products"
                className="inline-flex items-center justify-center gap-2 bg-primary hover:opacity-90 text-primary-foreground px-8 py-4 text-sm uppercase tracking-widest transition-colors"
                data-testid="landing-hero-cta"
              >
                {t("landing.hero_cta")} <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#peluang-reseller"
                className="inline-flex items-center justify-center gap-2 bg-black text-white px-8 py-4 text-sm uppercase tracking-widest hover:bg-black/85 transition-colors shadow-lg"
                data-testid="landing-hero-cta2"
              >
                {t("landing.brands_partner_cta")}
              </a>
            </div>
          </div>
        </div>

        {/* scroll indicator */}
        <div className="hidden sm:block absolute bottom-8 left-1/2 -translate-x-1/2 text-white/40 animate-bounce">
          <ChevronDown className="w-6 h-6" />
        </div>
      </section>

      {/* ───────────────── 2. BRAND VALUES ───────────────── */}
      <section className="py-20 sm:py-28 lg:py-32" data-testid="landing-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealSection className="text-center mb-16">
            <p className="font-sans text-[10px] sm:text-xs uppercase tracking-[0.3em] text-primary font-semibold mb-3">
              {t("landing.values_tag")}
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-stone-900 mb-4">
              {t("landing.values_title")}
            </h2>
            <div className="landing-divider mx-auto mt-6" />
          </RevealSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
            {[
              { icon: ShieldCheck, titleKey: "value_1_title", descKey: "value_1_desc" },
              { icon: Users, titleKey: "value_2_title", descKey: "value_2_desc" },
              { icon: PackageCheck, titleKey: "value_3_title", descKey: "value_3_desc" },
              { icon: Sparkles, titleKey: "value_4_title", descKey: "value_4_desc" },
            ].map((v, i) => (
              <RevealSection
                key={v.titleKey}
                delay={`landing-reveal-delay-${i + 1}`}
              >
                <div className="group text-center p-8 bg-white border border-stone-100 hover:border-primary/30 hover:shadow-sm transition-all duration-300">
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-primary/10 rounded-full mb-5 group-hover:bg-primary/20 transition-colors">
                    <v.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-serif text-lg text-stone-900 mb-3">
                    {t(`landing.${v.titleKey}`)}
                  </h3>
                  <p className="font-sans text-sm text-stone-500 leading-relaxed">
                    {t(`landing.${v.descKey}`)}
                  </p>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────── 6. PACKAGING TRUST ───────────────── */}
      <section
        className="py-20 sm:py-28 lg:py-32 bg-[#FAF9F6]"
        data-testid="landing-packaging-trust"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <RevealSection>
                <p className="font-sans text-[10px] sm:text-xs uppercase tracking-[0.3em] text-primary font-semibold mb-3">
                  {t("landing.packaging_tag")}
                </p>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-stone-900 mb-6 leading-tight">
                  {t("landing.packaging_title")}
                </h2>
                <p className="font-sans text-sm sm:text-base text-stone-600 leading-relaxed mb-8">
                  {t("landing.packaging_desc")}
                </p>

                {!isLoggedIn && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                      {[1, 2, 3].map((n) => (
                        <div key={n} className="flex items-start gap-3 p-4 bg-white border border-stone-100">
                          <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                          <span className="font-sans text-sm text-stone-700 leading-relaxed">{t(`landing.packaging_benefit_${n}`)}</span>
                        </div>
                      ))}
                    </div>

                    <Link
                      to="/login?mode=register&role=mitra"
                      className="inline-flex items-center gap-2 bg-primary hover:opacity-90 text-primary-foreground px-8 py-4 text-sm uppercase tracking-widest transition-colors"
                      data-testid="landing-packaging-cta"
                    >
                      {t("landing.packaging_cta")} <ArrowRight className="w-4 h-4" />
                    </Link>
                  </>
                )}
              </RevealSection>

              <RevealSection delay="landing-reveal-delay-2">
                <div className="space-y-4">
                  <div className="overflow-hidden bg-white p-3 shadow-sm border border-stone-100">
                    <img
                      src={PACKAGING_SHIPMENT_IMG}
                      alt={t("landing.packaging_shipment_alt")}
                      className="w-full aspect-[16/10] object-cover"
                      loading="lazy"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { src: PACKAGING_MAIN_IMG, alt: t("landing.packaging_main_alt") },
                      { src: PACKAGING_DETAIL_IMG, alt: t("landing.packaging_detail_alt") },
                      { src: PACKAGING_STACK_IMG, alt: t("landing.packaging_stack_alt") },
                    ].map((image) => (
                      <div key={image.src} className="overflow-hidden bg-white p-2 shadow-sm border border-stone-100">
                        <img
                          src={image.src}
                          alt={image.alt}
                          className="w-full aspect-square object-cover"
                          loading="lazy"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </RevealSection>
          </div>
        </div>
      </section>


      {/* ───────────────── 3. TRUSTED BRANDS ───────────────── */}
      <section id="daftar-brand" className="py-20 sm:py-28 bg-white scroll-mt-24" data-testid="landing-brands">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealSection className="text-center mb-16">
            <p className="font-sans text-[10px] sm:text-xs uppercase tracking-[0.3em] text-primary font-semibold mb-3">
              {t("landing.brands_tag")}
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-stone-900 mb-4">
              {t("landing.brands_title")}
            </h2>
            <p className="font-sans text-sm text-stone-500 max-w-2xl mx-auto leading-relaxed">
              {brands.length > 0
                ? `${t("landing.brands_desc_prefix")} 50+ ${t("landing.brands_desc_suffix")}`
                : t("landing.brands_empty")}
            </p>
            <div className="landing-divider mx-auto mt-6" />
          </RevealSection>

          {brands.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 lg:gap-5">
              {brands.slice(0, 12).map((brand, idx) => (
                <RevealSection key={brand.id} delay={`landing-reveal-delay-${(idx % 4) + 1}`}>
                  <Link
                    to={`/brands/${brand.id}`}
                    className="group h-full flex flex-col items-center justify-center text-center p-5 sm:p-6 bg-[#FAF9F6] border border-stone-100 hover:border-primary/30 hover:shadow-sm transition-all"
                    data-testid={`landing-brand-${brand.id}`}
                  >
                    <div className="w-14 h-14 rounded-full bg-white border border-stone-200 flex items-center justify-center mb-4 group-hover:border-primary/30 transition-colors">
                      <span className="font-serif text-lg text-stone-700 group-hover:text-primary transition-colors">
                        {brand.name?.charAt(0)?.toUpperCase() || "B"}
                      </span>
                    </div>
                    <h3 className="font-sans text-sm font-semibold text-stone-800 group-hover:text-primary transition-colors line-clamp-2">
                      {brand.name}
                    </h3>
                  </Link>
                </RevealSection>
              ))}
            </div>
          ) : (
            <RevealSection>
              <div className="text-center p-10 bg-[#FAF9F6] border border-stone-100">
                <ShieldCheck className="w-8 h-8 text-primary mx-auto mb-4" />
                <p className="font-sans text-sm text-stone-500">{t("landing.brands_empty")}</p>
              </div>
            </RevealSection>
          )}

          <RevealSection id="gabung-mitra-brand" className="text-center mt-10 scroll-mt-28">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/brands" className="inline-flex items-center gap-2 text-sm text-primary hover:opacity-80 uppercase tracking-widest">
                {t("landing.brands_cta")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/login?mode=register&intent=partner"
                className="inline-flex items-center justify-center gap-2 bg-primary hover:opacity-90 text-primary-foreground px-7 py-3 text-xs uppercase tracking-widest transition-colors"
                data-testid="landing-brands-partner-cta"
              >
                {t("landing.brands_partner_cta")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </RevealSection>
        </div>
      </section>

      {/* ───────────────── 4. FEATURED COLLECTION ───────────────── */}
      <section
        className="py-20 sm:py-28 bg-white"
        data-testid="landing-featured"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealSection className="text-center mb-16">
            <p className="font-sans text-[10px] sm:text-xs uppercase tracking-[0.3em] text-primary font-semibold mb-3">
              {t("landing.featured_tag")}
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-stone-900 mb-3">
              {t("landing.featured_title")}
            </h2>
            <p className="font-sans text-sm text-stone-500 max-w-md mx-auto">
              {t("landing.featured_desc")}
            </p>
            <div className="landing-divider mx-auto mt-6" />
          </RevealSection>

          <RevealSection>
            {products.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
                {products.slice(0, 4).map((p) => (
                  <ProductCard key={p.id} product={p} showReseller={isReseller} />
                ))}
              </div>
            ) : (
              /* Fallback placeholder cards */
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10">
                {FALLBACK_PRODUCTS.map((fp, i) => (
                  <Link
                    key={fp.id}
                    to="/products"
                    className="group block"
                  >
                    <div className="aspect-[3/4] bg-gradient-to-br from-stone-100 to-stone-200 mb-4 flex items-center justify-center">
                      <Sparkles className="w-8 h-8 text-stone-300 group-hover:text-primary transition-colors" />
                    </div>
                    <p className="font-sans text-xs text-stone-400 uppercase tracking-wider mb-1">
                      {fp.category}
                    </p>
                    <h3 className="font-sans text-sm font-medium text-stone-800 group-hover:text-primary transition-colors">
                      {t(`landing.featured_fallback_${i + 1}`)}
                    </h3>
                  </Link>
                ))}
              </div>
            )}
          </RevealSection>

          <RevealSection className="text-center mt-14">
            <Link
              to="/products"
              className="inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white px-10 py-4 text-sm uppercase tracking-widest transition-colors"
              data-testid="landing-featured-cta"
            >
              {t("landing.featured_cta")} <ArrowRight className="w-4 h-4" />
            </Link>
          </RevealSection>
        </div>
      </section>

      {/* ───────────────── 4. PREORDER HIGHLIGHT ───────────────── */}
      <section
        id="peluang-reseller"
        className="py-20 sm:py-28 lg:py-32 bg-[#FAF9F6] scroll-mt-24"
        data-testid="landing-preorder"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* image side */}
            <RevealSection>
              <div className="relative w-full max-w-xl mx-auto">
                <img
                  src={PREORDER_IMG}
                  alt="Pre-Order Collection"
                  className="w-full h-auto object-contain"
                />
                <div className="absolute top-6 left-6">
                  <span className="inline-block bg-white/90 backdrop-blur-sm text-stone-900 text-[10px] uppercase tracking-[0.2em] font-semibold px-4 py-2">
                    {t("landing.preorder_tag")}
                  </span>
                </div>
              </div>
            </RevealSection>

            {/* text side */}
            <RevealSection delay="landing-reveal-delay-2">
              <div className="lg:py-8">
                <p className="font-sans text-[10px] sm:text-xs uppercase tracking-[0.3em] text-amber-600 font-semibold mb-3">
                  {t("landing.preorder_tag")}
                </p>
                <h2 className="font-serif text-3xl sm:text-4xl text-stone-900 mb-6 leading-tight">
                  {t("landing.preorder_title")}
                </h2>
                <p className="font-sans text-sm sm:text-base text-stone-600 leading-relaxed mb-8 whitespace-pre-line">
                  {t("landing.preorder_desc")}
                </p>

                <div className="space-y-7 mb-10">
                  <div>
                    <h3 className="font-sans text-sm font-bold text-stone-800 mb-4">
                      {t("landing.preorder_benefit_heading")}
                    </h3>
                    <ul className="space-y-3">
                      {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                        <li key={n} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                          <span className="font-sans text-sm text-stone-700">
                            {t(`landing.preorder_benefit_${n}`)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-sans text-sm font-bold text-stone-800 mb-4">
                      {t("landing.preorder_suitable_heading")}
                    </h3>
                    <ul className="space-y-3">
                      {[1, 2, 3, 4].map((n) => (
                        <li key={n} className="flex items-start gap-3">
                          <Sparkles className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                          <span className="font-sans text-sm text-stone-700">
                            {t(`landing.preorder_suitable_${n}`)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <p className="font-sans text-sm sm:text-base text-stone-700 leading-relaxed bg-white/70 border border-stone-100 rounded-2xl px-4 py-3">
                    {t("landing.preorder_closing_note")}
                  </p>
                </div>

                <Link
                  to="/login?mode=register&intent=partner"
                  className="inline-flex items-center gap-2 bg-primary hover:opacity-90 text-primary-foreground px-8 py-4 text-sm uppercase tracking-widest transition-colors"
                  data-testid="landing-preorder-cta"
                >
                  {t("landing.preorder_cta")} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </RevealSection>
          </div>

          {/* preorder product cards if available */}
          {preorderProducts.length > 0 && (
            <RevealSection className="mt-16 sm:mt-20">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-12">
                {preorderProducts.slice(0, 3).map((p) => (
                  <ProductCard key={p.id} product={p} showReseller={isReseller} />
                ))}
              </div>
            </RevealSection>
          )}
        </div>
      </section>

      {/* ───────────────── 5. RESELLER / PARTNER ───────────────── */}
      {!isLoggedIn && (
        <section
          id="mitra"
          className="relative py-20 sm:py-28 lg:py-32 bg-stone-900 overflow-hidden"
          data-testid="landing-reseller"
        >
          {/* decorative bg */}
          <div className="absolute inset-0 opacity-[0.03]">
            <div className="w-full h-full"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}
            />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              {/* text */}
              <RevealSection>
                <p className="font-sans text-[10px] sm:text-xs uppercase tracking-[0.3em] text-primary font-semibold mb-3">
                  {t("landing.reseller_tag")}
                </p>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white mb-6 leading-tight">
                  {t("landing.reseller_title")}
                </h2>
                <p className="font-sans text-sm sm:text-base text-stone-400 leading-relaxed mb-10">
                  {t("landing.reseller_desc")}
                </p>

                <Link
                  to="/login?mode=register&role=mitra"
                  className="inline-flex items-center gap-2 bg-primary hover:opacity-90 text-primary-foreground px-8 py-4 text-sm uppercase tracking-widest transition-colors"
                  data-testid="landing-reseller-cta"
                >
                  {t("landing.reseller_cta")} <ArrowRight className="w-4 h-4" />
                </Link>
              </RevealSection>

              {/* benefits grid */}
              <RevealSection delay="landing-reveal-delay-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {[1, 2, 3, 4].map((n) => (
                    <div
                      key={n}
                      className="flex items-start gap-4 p-6 bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/10 transition-colors"
                    >
                      <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                      <span className="font-sans text-sm text-stone-300">
                        {t(`landing.reseller_benefit_${n}`)}
                      </span>
                    </div>
                  ))}
                </div>
              </RevealSection>
            </div>
          </div>
        </section>
      )}

      {/* ───────────────── 7. TESTIMONIALS ───────────────── */}
      <section
        className="py-20 sm:py-28 lg:py-32 bg-white"
        data-testid="landing-testimonials"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealSection className="text-center mb-16">
            <p className="font-sans text-[10px] sm:text-xs uppercase tracking-[0.3em] text-primary font-semibold mb-3">
              {t("landing.testimonial_tag")}
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-stone-900 mb-4">
              {t("landing.testimonial_title")}
            </h2>
            <div className="landing-divider mx-auto mt-6" />
          </RevealSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((n) => (
              <RevealSection
                key={n}
                delay={`landing-reveal-delay-${n}`}
              >
                <div className="p-8 bg-[#FAF9F6] border border-stone-100 h-full flex flex-col">
                  {/* stars */}
                  <div className="flex gap-1 mb-5">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className="w-4 h-4 fill-amber-400 text-amber-400"
                      />
                    ))}
                  </div>

                  {/* quote */}
                  <p className="font-sans text-sm text-stone-600 leading-relaxed flex-1 mb-6 italic">
                    "{t(`landing.testimonial_${n}_text`)}"
                  </p>

                  {/* author */}
                  <div className="flex items-center gap-3 pt-5 border-t border-stone-200">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="font-serif text-sm font-semibold text-primary">
                        {t(`landing.testimonial_${n}_name`).charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="font-sans text-sm font-semibold text-stone-800">
                        {t(`landing.testimonial_${n}_name`)}
                      </p>
                      <p className="font-sans text-xs text-stone-400 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {t(`landing.testimonial_${n}_loc`)}
                      </p>
                    </div>
                  </div>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────── 7. CLOSING CTA BANNER ───────────────── */}
      <section
        className="relative py-24 sm:py-32 lg:py-40 overflow-hidden"
        data-testid="landing-closing-cta"
      >
        {/* gradient bg */}
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, var(--tenant-primary-hex, #8A9A86) 0%, color-mix(in srgb, var(--tenant-primary-hex, #8A9A86) 78%, black) 45%, #44403c 100%)" }} />
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
          }}
        />

        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <RevealSection>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white mb-6 leading-tight">
              {t("landing.closing_title")}
            </h2>
            <p className="font-sans text-base sm:text-lg text-white/80 mb-10 max-w-xl mx-auto leading-relaxed">
              {t("landing.closing_desc")}
            </p>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 bg-white text-stone-900 hover:bg-stone-100 px-10 py-4 text-sm uppercase tracking-widest transition-colors font-medium"
              data-testid="landing-closing-cta-btn"
            >
              {t("landing.closing_cta")} <ArrowRight className="w-4 h-4" />
            </Link>
          </RevealSection>
        </div>
      </section>

      <Footer />
    </div>
  );
}
